import { ReactElement, lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress, Container, Toolbar, Typography } from '@mui/material';
import { observer } from 'mobx-react';

import Navbar from '../components/Navbar';
import CompanyRouter from './CompanyRouter';
import { useAuth } from '../stores';
import { LoginState } from '../stores/AuthStore';

const Home = lazy(() => import('../views/Home'));
const PasswordReset = lazy(() => import('../views/PasswordReset'));
const Subscriptions = lazy(() => import('../views/Admin/Subscriptions'));
const CreateCompany = lazy(() => import('../views/Company/CreateCompany'));
const Resellers = lazy(() => import('../views/Admin/Resellers'));
const ResellerDetails = lazy(() => import('../views/Admin/ResellerDetails'));
const ResellerInvoices = lazy(() => import('../views/Admin/ResellerInvoices'));
const ResellerProfile = lazy(() => import('../views/Reseller/ResellerProfile'));
const News = lazy(() => import('../views/News'));
const Search = lazy(() => import('../views/Search'));
const ResetPassword = lazy(() => import('../views/ResetPassword'));
const Login = lazy(() => import('../views/Login'));
const NotFound = lazy(() => import('../views/NotFound'));

const LoadingFallback = () => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      translate: '-50%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress color="info" sx={{ marginBottom: '1rem' }} />
    <Typography variant="subtitle1">Loading...</Typography>
  </Box>
);

const AdminRoutes = observer(() => {
  const authStore = useAuth();

  if (authStore.isAdmin) {
    return <Outlet />;
  }

  return <Navigate to="/" />;
});

const ResellerRoutes = observer(() => {
  const authStore = useAuth();

  if (authStore.isReseller) {
    return <Outlet />;
  }

  return <Navigate to="/reseller" />;
});

const AuthenticatedRoutes = observer(() => {
  const authStore = useAuth();

  if (authStore.isReseller || authStore.isAdmin) {
    return <Outlet />;
  }

  if (authStore.state === LoginState.LOADING || authStore.state === LoginState.LOADING_PERMISSION) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="subtitle1">
          {authStore.state === LoginState.LOADING && 'Loading...'}
          {authStore.state === LoginState.LOADING_PERMISSION && 'Loading permission...'}
        </Typography>
      </Box>
    );
  }
  return <Navigate to="/login" />;
});

const MainRouter = (): ReactElement => (
  <Box>
    <Navbar />
    <Container>
      <Toolbar />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<AuthenticatedRoutes />}>
            <Route element={<AdminRoutes />}>
              <Route path="/passwordreset" element={<PasswordReset />} />
              <Route path="/search" element={<Search />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/resellers" element={<Resellers />} />
              <Route path="/resellers/:resellerId" element={<ResellerDetails />} />
              <Route path="/resellers/:resellerId/invoices" element={<ResellerInvoices />} />
              <Route path="/resetpassword" element={<ResetPassword />} />
            </Route>
            <Route element={<ResellerRoutes />}>
              <Route path="/resellerProfile" element={<ResellerProfile />} />
            </Route>
            <Route path="/" element={<Home />} />
            <Route path="/create" element={<CreateCompany />} />
            <Route path="/news" element={<News />} />
            <Route path="company/:companyKey/*" element={<CompanyRouter />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Container>
  </Box>
);

export default MainRouter;
